import { z } from 'zod';

import { DateOnlySchema, IsoDateTimeSchema, UuidSchema } from './common.js';
import { IssueCardSchema } from './issue.js';

export const SprintSchema = z.object({
  id: UuidSchema,
  projectId: UuidSchema,
  name: z.string(),
  goal: z.string().nullable(),
  state: z.enum(['future', 'active', 'closed']),
  startDate: DateOnlySchema.nullable(),
  endDate: DateOnlySchema.nullable(),
  startedAt: IsoDateTimeSchema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
  hasSnapshot: z.boolean(),
  progress: z.object({
    total: z.number().int(),
    done: z.number().int(),
    points: z.number(),
    donePoints: z.number(),
  }),
  issues: z.array(IssueCardSchema).optional(),
  createdAt: IsoDateTimeSchema,
});
export type Sprint = z.infer<typeof SprintSchema>;

export const CreateSprintBodySchema = z
  .object({
    name: z.string().min(1, 'Введите название').max(100),
    goal: z.string().max(2000).nullable().default(null),
    startDate: DateOnlySchema.nullable().default(null),
    endDate: DateOnlySchema.nullable().default(null),
  })
  .strict();
export type CreateSprintBody = z.infer<typeof CreateSprintBodySchema>;

export const UpdateSprintBodySchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    goal: z.string().max(2000).nullable().optional(),
    startDate: DateOnlySchema.nullable().optional(),
    endDate: DateOnlySchema.nullable().optional(),
  })
  .strict();
export type UpdateSprintBody = z.infer<typeof UpdateSprintBodySchema>;

export const SprintIssuesBodySchema = z
  .object({
    issueIds: z.array(UuidSchema).min(1, 'Выберите хотя бы одну задачу').max(500),
  })
  .strict();
export type SprintIssuesBody = z.infer<typeof SprintIssuesBodySchema>;

export const CompleteSprintBodySchema = z
  .object({
    moveTo: z.enum(['backlog', 'sprint', 'new']).default('backlog'),
    targetSprintId: UuidSchema.nullable().default(null),
    newSprintName: z.string().min(1).max(100).nullable().default(null),
  })
  .strict();
export type CompleteSprintBody = z.infer<typeof CompleteSprintBodySchema>;

export const CompleteSprintResponseSchema = z.object({
  sprint: SprintSchema,
  moved: z.number().int(),
  completed: z.number().int(),
  target: z
    .object({
      type: z.enum(['backlog', 'sprint', 'new']),
      id: UuidSchema.nullable(),
      name: z.string().nullable(),
    })
    .nullable(),
});
export type CompleteSprintResponse = z.infer<typeof CompleteSprintResponseSchema>;

export const BulkUpdateIssuesBodySchema = z
  .object({
    issueIds: z.array(UuidSchema).min(1).max(500),
    patch: z
      .object({
        assigneeId: UuidSchema.nullable().optional(),
        priority: z.enum(['lowest', 'low', 'medium', 'high', 'highest']).optional(),
        sprintId: UuidSchema.nullable().optional(),
        toStatusId: UuidSchema.optional(),
      })
      .strict(),
  })
  .strict();
export type BulkUpdateIssuesBody = z.infer<typeof BulkUpdateIssuesBodySchema>;

export const BulkUpdateResultSchema = z.object({
  updated: z.array(z.string()),
  failed: z.array(z.object({ key: z.string(), message: z.string() })),
});
export type BulkUpdateResult = z.infer<typeof BulkUpdateResultSchema>;

export const VelocitySchema = z.object({
  sprints: z.array(
    z.object({
      id: UuidSchema,
      name: z.string(),
      completedAt: IsoDateTimeSchema.nullable(),
      committed: z.number(),
      completed: z.number(),
    }),
  ),
  average: z.number(),
});
export type Velocity = z.infer<typeof VelocitySchema>;
